/**
 * The disclosure cluster of the work record — who sees which line, and which
 * fields of it — wired to the three calls on `/works/:id/disclosure`.
 *
 * COMPOSED BY `useWorkController.ts`, like `useWorkAgents`, and it owns the
 * response, its own busy flag and the draft of the rule being written. It does
 * NOT own a second error channel: a failed write is a sentence on the panel,
 * because the thing that failed is one rule rather than the record.
 *
 * IT ADOPTS ONLY WHAT THE SERVER SENT BACK. Every one of the three calls
 * returns the whole panel, so nothing here predicts what a write did — which
 * matters most for a delegated agent, whose rule only ever narrows and whose
 * effective answer is therefore the union of theirs and the owner's. A hook
 * that patched its own state optimistically would be computing the precedence
 * rule in the browser.
 *
 * A 403 IS NOT RETRIED AND NOT EXPLAINED. The server gives one refusal for
 * every way of not being allowed, and inventing a reason for it here would be
 * the client guessing at an authorisation it does not decide.
 */

import { useCallback, useEffect, useState } from "react";

import {
  clearWorkDisclosureRule,
  fetchWorkDisclosure,
  setWorkDisclosureRule,
} from "../lib/api.ts";
import type {
  DisclosureAudience,
  ProvenanceEntry,
  ProvenanceField,
  WorkDisclosureResponse,
} from "@ondo/shared";
import { toDisclosurePanel } from "./disclosure.model.ts";
import type { DisclosurePanel } from "./disclosure.model.ts";

/** The rule being written, as the form holds it. */
export interface RuleDraft {
  /** Null is the default every authorised viewer inherits. */
  audience: DisclosureAudience | null;
  /** Null is every entry on the work. */
  entryId: string | null;
  hidden: boolean;
  /** A set rather than an array, because the form toggles one field at a time. */
  hiddenFields: ProvenanceField[];
}

export const EMPTY_RULE_DRAFT: RuleDraft = {
  audience: null,
  entryId: null,
  hidden: false,
  hiddenFields: [],
};

export interface DisclosureHandlers {
  onSetAudience: (audience: DisclosureAudience | null) => void;
  onSetEntry: (entryId: string | null) => void;
  onSetHidden: (hidden: boolean) => void;
  onToggleField: (field: ProvenanceField) => void;
  onSave: () => void;
  onRemove: (ruleId: string) => void;
}

export function useWorkDisclosure(workId: string | null, entries: ProvenanceEntry[]) {
  /**
   * THE WORK IT IS ABOUT IS STORED WITH IT, AND NOT CLEARED BY AN EFFECT.
   *
   * `useMe` derives `loading` from a mismatch rather than holding a flag, and
   * this is the same device for the same reason: clearing the response
   * synchronously when `workId` changes is a `setState` inside an effect, which
   * cascades a render — and on a disclosure panel the cost of the alternative
   * is worse than a wasted render. A response kept from the PREVIOUS work would
   * draw one work's rules under another work's title for one frame.
   */
  const [loaded, setLoaded] = useState<{
    workId: string;
    body: WorkDisclosureResponse;
  } | null>(null);
  const [draft, setDraft] = useState<RuleDraft>(EMPTY_RULE_DRAFT);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const response = loaded !== null && loaded.workId === workId ? loaded.body : null;

  // THE `active` GUARD, as every effect in this app has: a response for a work
  // the reader has navigated away from must not write state. `useMe` and
  // `useSession` are the pattern.
  useEffect(() => {
    if (workId === null) return;

    let active = true;
    // A REFUSAL LEAVES THE PANEL NULL rather than showing an empty one. A
    // caller who may not manage these rules gets a 403, and `canManage: false`
    // and "no panel" are different things — the first is a permission the
    // screen can explain, the second is a work whose rules are somebody else's
    // business entirely.
    fetchWorkDisclosure(workId)
      .then((next) => {
        if (active) setLoaded({ workId, body: next });
      })
      .catch(() => {
        if (active) setLoaded(null);
      });

    return () => {
      active = false;
    };
  }, [workId]);

  const save = useCallback(() => {
    if (workId === null) return;

    setBusy(true);
    setFailed(false);
    setWorkDisclosureRule(workId, draft)
      .then((next) => {
        setLoaded({ workId, body: next });
        // The draft is cleared only on success, so a refused rule is still in
        // the form to be corrected — the same call `useWorkAgents` makes about
        // the assign modal: closing is not discarding.
        setDraft(EMPTY_RULE_DRAFT);
      })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  }, [workId, draft]);

  const remove = useCallback(
    (ruleId: string) => {
      if (workId === null) return;

      setBusy(true);
      setFailed(false);
      clearWorkDisclosureRule(workId, ruleId)
        .then((next) => setLoaded({ workId, body: next }))
        .catch(() => setFailed(true))
        .finally(() => setBusy(false));
    },
    [workId],
  );

  const handlers: DisclosureHandlers = {
    onSetAudience: (audience) => setDraft((current) => ({ ...current, audience })),
    onSetEntry: (entryId) => setDraft((current) => ({ ...current, entryId })),
    onSetHidden: (hidden) => setDraft((current) => ({ ...current, hidden })),
    onToggleField: (field) =>
      setDraft((current) => ({
        ...current,
        hiddenFields: current.hiddenFields.includes(field)
          ? current.hiddenFields.filter((each) => each !== field)
          : [...current.hiddenFields, field],
      })),
    onSave: save,
    onRemove: remove,
  };

  const panel: DisclosurePanel | null = toDisclosurePanel(response, entries);

  return { panel, draft, busy, failed, handlers };
}
